/**
 * Safely provisions headless DOM mocks in Node.js environments
 * so Phaser 4 in Phaser.HEADLESS mode can instantiate and run without errors.
 */
export function ensureHeadlessEnvironment(): void {
  const g = globalThis as Record<string, unknown>;
  if (typeof g.window !== 'undefined') {
    return; // Already in browser or polyfilled environment
  }

  const win = g as Record<string, unknown>;
  g.window = g;
  win.addEventListener = () => {};
  win.removeEventListener = () => {};
  g.screen = { width: 1920, height: 1080 };

  class FakeImage {}
  class FakeVideo {}

  const fakeCtx = {
    fillRect: () => {},
    getImageData: () => ({ data: new Uint8Array(4) }),
    putImageData: () => {},
    canvas: {},
    save: () => {},
    restore: () => {},
    beginPath: () => {},
    closePath: () => {},
    moveTo: () => {},
    lineTo: () => {},
    stroke: () => {},
    fill: () => {},
    clearRect: () => {},
    scale: () => {},
    translate: () => {},
    drawImage: () => {},
  };
  (fakeCtx.canvas as Record<string, unknown>).getContext = () => fakeCtx;

  class FakeCanvas {
    width = 640;
    height = 480;
    style = {};
    parentNode = { removeChild: () => {} };
    addEventListener() {}
    removeEventListener() {}
    getContext() {
      return fakeCtx;
    }
    getBoundingClientRect() {
      return { top: 0, left: 0, width: this.width, height: this.height, right: this.width, bottom: this.height };
    }
  }

  g.Image = FakeImage;
  g.HTMLCanvasElement = FakeCanvas;
  g.HTMLVideoElement = FakeVideo;

  const body = {
    appendChild: () => {},
    removeChild: () => {},
    style: {},
    getBoundingClientRect: () => ({ top: 0, left: 0, width: 640, height: 480, right: 640, bottom: 480 }),
  };

  g.document = {
    readyState: 'complete',
    addEventListener: () => {},
    removeEventListener: () => {},
    createElement: (tag: string) => {
      if (tag === 'canvas') {
        return new FakeCanvas();
      }
      return {
        style: {},
        appendChild: () => {},
        removeChild: () => {},
        getBoundingClientRect: () => ({ top: 0, left: 0, width: 640, height: 480, right: 640, bottom: 480 }),
      };
    },
    documentElement: { style: {} },
    body,
  };

  if (!('navigator' in g) || !(g.navigator as Record<string, unknown>)?.userAgent) {
    try {
      Object.defineProperty(g, 'navigator', {
        value: { userAgent: 'node' },
        configurable: true,
        writable: true,
      });
    } catch {
      // Node 24 navigator already present
    }
  }
}

// Automatically ensure environment for headless/Node evaluation
ensureHeadlessEnvironment();

